import { useFerve } from "@features/ferve/FerveProvider"
import { formatDollars } from "@utils/money-format"
import { cx, tv } from "@utils/tw"
import { motion } from "motion/react"
import { useEffect, useId, useState } from "react"
import { useCartState } from "./useCartState"
import { Link } from "eddev/routing"

export function InCartDonation() {
  const cart = useFerve().cart
  const state = useCartState()

  if (!state.data?.shouldPromptCartDonation) return null

  return (
    <motion.div className="rounded-md bg-bg border border-bg-mid" layoutRoot>
      <div className="flex gap-4 type-ui-m p-3">
        {/* Details */}
        <div className="flex flex-col gap-2 flex-1">
          <div>Make a tax-deductible donation</div>
          <div className="type-ui-s text-fg-secondary basic-text-styles">
            Round up your ticket and support SFF's{" "}
            <Link href="/support-us/individual-support/sff-admit-all/">Film For All</Link> program to help bring
            underrepresented communities to the Festival.
          </div>
        </div>
      </div>
      <div className="border-t border-bg-low p-3 md:p-2">
        <InCartDonationSelector />
      </div>
    </motion.div>
  )
}

type Option = {
  price: number | "roundup" | "other"
  label?: string
}

const OPTIONS: Option[] = [
  {
    price: "roundup",
    label: "Round-up purchase",
  },
  {
    price: 10,
  },
  {
    price: 26.5,
    label: "Average ticket cost",
  },
  {
    price: "other",
  },
]

export function InCartDonationSelector() {
  const cart = useFerve().cart
  const state = useCartState()

  if (!state.data?.shouldPromptCartDonation) return null

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map((option, i) => {
          let isSelected = false
          let price = 0
          let onSelect = (value?: number) => {}
          if (option.price === "roundup") {
            isSelected = state.donationPrefs.mode === "roundup"
            price = state.data?.potentialRoundUpAmount ?? 0
            onSelect = () => {
              cart.setDonationPrefs({
                mode: "roundup",
              })
            }
          } else if (option.price === "other") {
            isSelected = state.donationPrefs.mode === "other" && state.donationPrefs.mode === "other"
            price = state.donationPrefs.mode === "other" ? (state.donationPrefs.amount ?? 0) : 0
            onSelect = (value) => {
              cart.setDonationPrefs({
                mode: "other",
                amount: value,
              })
            }
          } else {
            isSelected = state.donationPrefs.mode === "preset" && state.donationPrefs.amount === option.price
            price = option.price
            onSelect = () => {
              cart.setDonationPrefs({
                mode: "preset",
                amount: option.price as number,
              })
            }
          }
          return (
            <RadioButton
              key={i}
              selected={isSelected}
              onSelect={onSelect}
              isOther={option.price === "other"}
              className="flex-1"
              price={price}
              label={option.label}
            />
          )
        })}
        <RadioButton
          selected={state.donationPrefs.mode === "no-thanks"}
          className="col-span-2"
          onSelect={() => {
            cart.setDonationPrefs({
              mode: "no-thanks",
            })
          }}
          label="No thanks"
        />
      </div>
    </div>
  )
}

type RadioButtonProps = {
  price?: number
  label?: string
  selected: boolean
  onSelect: (value: number) => void
  className?: string
  isOther?: boolean
}

function RadioButton(props: RadioButtonProps) {
  const id = useId()

  const styles = buttonStyles({
    hasPrice: typeof props.price === "number",
    selected: props.selected,
  })

  let price = props.price ?? 0

  const [customValue, setCustomValue] = useState(props.price)
  useEffect(() => {
    if (props.isOther && props.price) {
      setCustomValue(props.price)
    }
  }, [props.price, props.isOther])
  if (props.isOther && customValue) {
    price = customValue
  }
  useEffect(() => {
    if (props.isOther && props.selected) {
      let timer = setTimeout(() => {
        props.onSelect(customValue ?? 0)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [props.isOther, customValue])

  let contents = (
    <>
      {typeof props.price === "number" && <span className={styles.price()}>{formatDollars(props.price)}</span>}
      {!!props.label && <span className={styles.label()}>{props.label}</span>}
    </>
  )

  if (props.isOther) {
    if (!props.selected) {
      contents = (
        <>
          <span className={styles.price({ className: "inline-flex items-center gap-1" })}>Other</span>
          <span className={styles.label()}>Custom amount</span>
        </>
      )
    } else {
      contents = (
        <>
          <span className={styles.price({ className: "inline-flex items-center gap-1 type-body-m [font-size:16px]" })}>
            <span className="flex-none">$</span>
            <input
              type="text"
              defaultValue={customValue === 0 ? "" : customValue?.toFixed(2).replace(/\.00$/, "")}
              onChange={(e) => {
                if (!isNaN(Number(e.target.value))) {
                  setCustomValue(Number(e.target.value))
                }
              }}
              placeholder=""
              className="outline-none bg-transparent appearance-none p-0 m-0 w-10 [font:inherit] [font-size:inherit] tracking-[0.04em] leading-[1.2] h-[1.2em]"
              inputMode="numeric"
              pattern="[0-9]*(\.[0-9])?"
              autoFocus
              onBlur={(e) => {
                const normalized = customValue === 0 ? "" : String(customValue?.toFixed(2).replace(/\.00$/, ""))
                e.currentTarget.value = normalized
                setCustomValue(Number(normalized))
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur()
                }
              }}
            />
          </span>
          <span className={styles.label()}>Custom amount</span>
        </>
      )
    }
  }

  return (
    <label className={cx(styles.base(), props.className)}>
      <input
        id={id}
        type="radio"
        name="in_cart_donation"
        checked={props.selected}
        className="hidden"
        value={props.price}
        onChange={() => {
          if (props.isOther) {
            props.onSelect(customValue ?? 0)
          } else {
            props.onSelect(props.price ?? 0)
          }
        }}
      />
      <div className="flex flex-1 flex-col gap-1">{contents}</div>
      <span className={styles.radio()} aria-hidden="true" />
    </label>
  )
}

const buttonStyles = tv({
  base: "flex items-center gap-[8px] rounded-[12px] px-[12px] text-left border border-bg-low py-[4px] type-ui-m cursor-pointer transition-colors",
  slots: {
    price: "type-body-m leading-[1.2] h-[1.2em]",
    label: "type-body-s",
    radio: [
      "size-[12px] rounded-full border border-orange bg-bg flex flex-none items-center justify-center",
      "before:content-space before:block before:size-[8px] before:rounded-full before:bg-orange before:transition-transform before:duration-200 before:ease-in-out before:scale-0",
    ],
  },
  variants: {
    selected: {
      true: {
        base: "bg-orange/20 border-orange",
        radio: "before:scale-100",
      },
      false: "hover:border-bg-mid",
    },
    hasPrice: {
      true: {
        base: "min-h-[55px]",
      },
      false: {
        base: "h-control",
        label: "type-ui-m text-fg",
      },
    },
  },
})
